import GuideLayout, {
  H2,
  H3,
  P,
  UL,
  OL,
  Callout,
  ExternalLink,
  Strong,
  Em,
} from "../ui/GuideLayout.jsx";

export default function AbrirContaBanco() {
  return (
    <GuideLayout
      title="Como abrir conta bancária no Japão"
      subtitle="Os bancos que aceitam estrangeiro recém-chegado, documentos necessários, e por que Yucho é quase sempre o primeiro passo."
      slug="abrir-conta-banco-japao"
      description="Guia prático sobre como abrir conta bancária no Japão sendo brasileiro. Yucho, Shinsei, SMBC, MUFG, requisitos e documentos."
      updated="Maio de 2026"
      readTime="9 min"
    >
      <P>
        Você chegou no Japão. Tem zairyu card, juuminhyou, número de
        celular — mas ainda nenhum lugar pra receber salário, pagar conta,
        ou guardar dinheiro. Abrir conta bancária no Japão sendo
        estrangeiro tem regras específicas que mudam por banco.
      </P>
      <P>
        Este guia mostra o que cada banco aceita, qual escolher, e como
        evitar a situação clássica de recusa por &ldquo;menos de 6 meses no
        Japão&rdquo;.
      </P>

      <H2 id="regra-6-meses">A regra dos 6 meses (e como contorná-la)</H2>
      <P>
        Quase todos os bancos comerciais japoneses (MUFG, SMBC, Mizuho)
        recusam abrir conta para estrangeiro com menos de 6 meses de
        residência no Japão. Isso é regulamentação anti-lavagem de dinheiro.
      </P>
      <P>
        <Strong>Exceções importantes:</Strong>
      </P>
      <UL>
        <li>
          <Strong>Yucho (post bank):</Strong> abre conta para qualquer
          estrangeiro com zairyu, mesmo no primeiro dia
        </li>
        <li>
          <Strong>Shinsei Bank:</Strong> aceita estrangeiros recentes sem a
          regra rígida dos 6 meses
        </li>
        <li>
          <Strong>SBI Sumishin Net Bank, Sony Bank:</Strong> também aceitam
          relativamente cedo
        </li>
      </UL>
      <Callout>
        Estratégia padrão: abra conta no <Strong>Yucho primeiro</Strong> (o
        mais fácil), use ele pra receber salário e pagar contas inicialmente.
        Depois de 6 meses, abre conta secundária em banco maior se precisar.
      </Callout>

      <H2 id="yucho">Yucho (Japan Post Bank / ゆうちょ銀行)</H2>
      <P>
        É o banco da agência dos correios. Tem agência em literalmente toda
        cidade do Japão, até em vilarejos rurais. Maior rede de ATMs do país.
      </P>

      <H3>Documentos para abrir Yucho</H3>
      <OL>
        <li>
          <Strong>Zairyu card</Strong> (em vigor)
        </li>
        <li>
          <Strong>Passaporte</Strong>
        </li>
        <li>
          <Strong>Juuminhyou</Strong> (住民票) — emitido nos últimos 6 meses,
          do endereço atual
        </li>
        <li>
          <Strong>Inkan</Strong> (carimbo pessoal) — ou pode assinar em alguns casos
        </li>
        <li>
          <Strong>Número de telefone japonês</Strong> em uso
        </li>
        <li>
          <Strong>My Number card ou notification</Strong> (recomendado, em
          alguns casos exigido)
        </li>
      </OL>

      <H3>Vantagens do Yucho</H3>
      <UL>
        <li>Aceita brasileiros recém-chegados sem complicação</li>
        <li>Maior rede de ATMs do Japão (em quase todo conbini também)</li>
        <li>Suporte parcial em inglês em agências grandes</li>
        <li>Transferência internacional pra Brasil (mais cara que Wise, mas existe)</li>
        <li>Sem mensalidade na conta básica</li>
      </UL>

      <H3>Desvantagens</H3>
      <UL>
        <li>App mobile japonês básico</li>
        <li>Internet banking limitado (precisa cadastro presencial)</li>
        <li>Transferência internacional sai caro (¥2.000–4.000 + spread)</li>
        <li>Para receber salário grande de empresa, alguns RHs preferem outros bancos</li>
      </UL>

      <H2 id="shinsei">Shinsei Bank (SBI Shinsei)</H2>
      <P>
        Banco focado em pessoa física, com plataforma online completa em
        inglês. Bom para quem quer gerenciar tudo pelo celular sem ir em agência.
      </P>

      <H3>Vantagens</H3>
      <UL>
        <li>Interface 100% em inglês</li>
        <li>Aceita estrangeiros com poucos meses no Japão</li>
        <li>ATM Bank Shinsei tem em conbini e estações</li>
        <li>Transferência internacional pelo GoRemit (parceiro)</li>
        <li>Conta em USD, EUR, GBP possível (multi-currency)</li>
      </UL>

      <H3>Desvantagens</H3>
      <UL>
        <li>Sem agências físicas em muitas cidades (só grandes)</li>
        <li>Atendimento humano demorado</li>
        <li>Cartão de débito chega por correio em 1-2 semanas</li>
      </UL>

      <H2 id="bancos-grandes">Bancos grandes tradicionais (MUFG, SMBC, Mizuho)</H2>
      <P>
        Os três megabancos japoneses. Têm tudo (agências, empréstimos,
        cartões, financiamento de casa), mas são burocráticos para
        estrangeiro recente.
      </P>

      <H3>Quando vale abrir</H3>
      <UL>
        <li>Você está há mais de 6 meses no Japão</li>
        <li>Seu RH manda você abrir conta lá (algumas empresas têm preferência)</li>
        <li>Você quer financiamento de carro ou casa</li>
        <li>Você fala japonês razoável (atendimento é em japonês)</li>
      </UL>

      <H3>Documentos típicos</H3>
      <OL>
        <li>Zairyu card</li>
        <li>Passaporte</li>
        <li>Juuminhyou recente</li>
        <li>Inkan (não aceita só assinatura)</li>
        <li>Comprovante de endereço (conta de luz, contrato de aluguel)</li>
        <li>Comprovante de emprego (certificado da empresa)</li>
        <li>My Number (obrigatório agora)</li>
        <li>Telefone japonês</li>
      </OL>

      <H2 id="bancos-online">Bancos digitais (Rakuten, PayPay, SBI)</H2>
      <P>
        Tendência crescente, especialmente entre os mais jovens. Vantagem:
        sem agência, tudo no app, taxas menores, cashback em compras.
      </P>
      <UL>
        <li>
          <Strong>Rakuten Bank:</Strong> integrado ao ecossistema Rakuten
          (compras, cartão de crédito, ponto). Aceita estrangeiros com
          residência verificada. App em inglês limitado.
        </li>
        <li>
          <Strong>PayPay Bank:</Strong> parte do SoftBank. App moderno,
          QR Code pagamentos integrados (PayPay é o sistema de pagamento mais
          usado no Japão). Aceita estrangeiros mas processo só em japonês.
        </li>
        <li>
          <Strong>SBI Sumishin Net Bank:</Strong> conta online com bom
          rendimento (yuubin chochiku), tem app, aceita estrangeiros.
        </li>
      </UL>

      <H2 id="inkan">Sobre o inkan (carimbo pessoal)</H2>
      <P>
        Inkan (印鑑) ou hanko (判子) é o carimbo pessoal que substitui
        assinatura no Japão. Você precisa pra abrir conta, alugar
        apartamento, comprar carro, e muitos outros documentos formais.
      </P>
      <P>
        <Strong>Como conseguir:</Strong>
      </P>
      <UL>
        <li>
          <Strong>Mitomein</Strong> (認印 — carimbo informal): ¥500–2.000 em
          qualquer loja de inkan. Tem em shopping, donki, lojinhas. Você
          escolhe a fonte (katakana, kanji se você tem nome em kanji).
        </li>
        <li>
          <Strong>Ginko-in</Strong> (銀行印 — carimbo bancário específico):
          recomendado ter um diferente do mitomein, registrado só nos bancos.
          ¥3.000–8.000.
        </li>
        <li>
          <Strong>Jitsuin</Strong> (実印 — carimbo registrado em cartório):
          obrigatório pra carro, casa. Você registra na prefeitura. Custo:
          ¥3.000–8.000 + taxa de registro ¥300.
        </li>
      </UL>
      <P>
        Para brasileiro, geralmente o nome é feito em <Em>katakana</Em>{" "}
        (シルバ JOSÉ, por exemplo). Se for nome longo, abrevia.
      </P>

      <H2 id="receber-salario">Receber salário</H2>
      <P>
        Quase toda fábrica deposita salário direto na conta. No primeiro
        contracheque, eles te pedem:
      </P>
      <UL>
        <li>Nome do banco (em katakana)</li>
        <li>Código da agência (4 dígitos)</li>
        <li>Número da conta (7 dígitos)</li>
        <li>Tipo de conta (普通 / futsu para conta corrente — quase sempre)</li>
        <li>Nome completo do titular (em katakana)</li>
      </UL>
      <P>
        Yucho usa formato diferente — você precisa do <Em>furikae</Em>{" "}
        (振替) ou converter pra formato banco. Algumas fábricas têm balcão
        que ajuda com isso.
      </P>

      <H2 id="receber-do-brasil">Receber dinheiro do Brasil</H2>
      <P>
        Inverso da remessa que você manda. Da família ou pra emergência.
        Opções:
      </P>
      <UL>
        <li>
          <Strong>Wise (recomendado):</Strong> família no Brasil deposita em
          Reais → você recebe em ienes no Yucho ou banco maior. Câmbio
          competitivo, taxa baixa. Demora 1-2 dias.
        </li>
        <li>
          <Strong>Western Union:</Strong> mais caro mas instantâneo. Família
          deposita em qualquer agência ou Pix, você retira em conbini.
        </li>
        <li>
          <Strong>Banco tradicional brasileiro:</Strong> Itaú, Bradesco têm
          transferência internacional pesada, ¥6.000–10.000 de taxa. Evite
          se possível.
        </li>
      </UL>

      <H2 id="cartao-credito">Sobre cartão de crédito</H2>
      <P>
        Cartão de crédito (kurejitto kaado) é difícil pra estrangeiro
        recente. Bancos pedem 1-3 anos de histórico no Japão antes de
        aprovar. Alternativas:
      </P>
      <UL>
        <li>
          <Strong>Cartão de débito do banco:</Strong> vem junto com a conta.
          Não tem limite de crédito, debita direto. Funciona em quase todo
          lugar que aceita VISA/Mastercard.
        </li>
        <li>
          <Strong>Prepaid (PayPay, Suica, ICOCA):</Strong> recarregável,
          funciona pra compras pequenas e transporte.
        </li>
        <li>
          <Strong>Cartão de crédito de empresa:</Strong> algumas grandes
          empresas (Toyota, Honda) emitem cartão de crédito como benefício.
          Pergunta se a sua oferece.
        </li>
        <li>
          <Strong>Rakuten Card:</Strong> dos cartões de crédito tradicionais,
          é o mais flexível com estrangeiros. Tente após 6 meses no Japão.
        </li>
      </UL>

      <H2 id="armadilhas">Armadilhas comuns</H2>
      <UL>
        <li>
          <Strong>Pagar mensalidade sem precisar:</Strong> contas básicas em
          quase todos os bancos são grátis. Se alguém te oferece &ldquo;pacote
          premium&rdquo; com mensalidade, recuse.
        </li>
        <li>
          <Strong>Não atualizar endereço no banco:</Strong> mudou de
          apartamento? Avise o banco em 14 dias, junto com a prefeitura.
          Banco que tem endereço errado pode bloquear conta.
        </li>
        <li>
          <Strong>Não atualizar zairyu novo:</Strong> renovou seu zairyu card?
          Vai no banco com o novo. Eles cruzam dados periodicamente.
        </li>
        <li>
          <Strong>Esquecer senha do internet banking:</Strong> reset presencial
          em agência, demora. Anote em lugar seguro.
        </li>
        <li>
          <Strong>ATM fora do horário:</Strong> alguns bancos cobram ¥110–220
          por saque fora do horário comercial. Yucho ATMs em conbini funcionam
          24/7 mas com taxa noturna em alguns.
        </li>
      </UL>

      <H2 id="conclusao">Resumindo</H2>
      <OL>
        <li><Strong>Primeiro passo:</Strong> Yucho. Vai numa agência dos correios com zairyu, juuminhyou, passaporte, inkan.</li>
        <li>Use o Yucho pra receber salário e pagar contas no início.</li>
        <li>Depois de 6 meses no Japão: considere Shinsei pra inglês ou MUFG/SMBC se você precisa de financiamento.</li>
        <li>Cartão de débito do banco resolve quase tudo. Cartão de crédito vem depois de 1-2 anos.</li>
        <li>Lembre de atualizar endereço e zairyu no banco em toda mudança.</li>
      </OL>
    </GuideLayout>
  );
}
